import { useEffect, useMemo, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { getUsersApi, useCore } from "@praesto/core";
import type { Department, WorkloadAgg } from "@praesto/shared-types";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Button,
  StatCard,
  EmptyState,
  SkeletonStatGrid,
  departmentNameResolver,
} from "@praesto/ui-kit";

interface ShiftLite {
  id: string;
  date: string;
  slot: "M" | "P" | "N" | "X";
  hotelId?: string;
  departmentId?: string;
  status?: string;
}

function mondayOf(date: Date): string {
  const d = new Date(date);
  const dow = d.getUTCDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

function addDaysISO(date: string, n: number): string {
  const [y, m, day] = date.split("-").map(Number);
  const t = new Date(Date.UTC(y!, m! - 1, day! + n));
  return t.toISOString().slice(0, 10);
}

export function ProfileWorkloadTab() {
  const core = useCore();
  const { uid } = useParams({ strict: false }) as { uid: string };
  const today = new Date().toISOString().slice(0, 10);
  const [weekStart, setWeekStart] = useState<string>(() => mondayOf(new Date()));
  const [workload, setWorkload] = useState<WorkloadAgg | null>(null);
  const [shifts, setShifts] = useState<ShiftLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const [wlRes, shiftsRes] = await Promise.all([
        fetch(`${api.apiBase}/api/users/${uid}/workload?weekStart=${weekStart}&days=7`, {
          headers: { Authorization: `Bearer ${tok}` },
        }).then((r) => r.json() as Promise<WorkloadAgg>),
        fetch(
          `${api.apiBase}/api/users/${uid}/shifts?weekStart=${addDaysISO(weekStart, -7)}&days=21`,
          {
            headers: { Authorization: `Bearer ${tok}` },
          },
        ).then((r) => r.json() as Promise<{ shifts: ShiftLite[] }>),
      ]);
      if (!cancelled) {
        setWorkload(wlRes);
        setShifts(shiftsRes.shifts.slice().sort((a, b) => a.date.localeCompare(b.date)));
      }
    })()
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [uid, weekStart]);

  // Shifts carry the department code; show the name each hotel gave it.
  const shiftHotelsKey = useMemo(
    () => [...new Set(shifts.flatMap((s) => (s.hotelId ? [s.hotelId] : [])))].sort().join("|"),
    [shifts],
  );
  const [deptsByHotel, setDeptsByHotel] = useState<ReadonlyMap<string, ReadonlyArray<Department>>>(
    new Map(),
  );
  useEffect(() => {
    if (shiftHotelsKey === "") return;
    let cancelled = false;
    void Promise.all(
      shiftHotelsKey
        .split("|")
        .map(async (h) => [h, await core.organization.departments(h).catch(() => [])] as const),
    ).then((entries) => {
      if (!cancelled) setDeptsByHotel(new Map(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [core, shiftHotelsKey]);
  const deptLabel = (hotelId: string | undefined, code: string): string =>
    departmentNameResolver((hotelId && deptsByHotel.get(hotelId)) || [])(code);

  if (loading) return <SkeletonStatGrid count={4} />;
  if (error) return <p className="text-sm text-danger">{error}</p>;
  if (!workload) return null;

  const past = shifts.filter((s) => s.date < today);
  const upcoming = shifts.filter((s) => s.date >= today);

  return (
    <div className="space-y-6 max-w-3xl">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-eyebrow">Settimana del {formatItalianDate(weekStart)}</div>
        <div className="flex gap-2">
          <Button
            size="md"
            variant="ghost"
            className="tap-target px-3 sm:h-9"
            aria-label={core.i18n.t("people.nav.week_prev")}
            onClick={() => setWeekStart(addDaysISO(weekStart, -7))}
          >
            <ChevronLeft size={16} aria-hidden />
          </Button>
          <Button
            size="md"
            variant="secondary"
            className="flex-1 sm:flex-none sm:h-9"
            onClick={() => setWeekStart(mondayOf(new Date()))}
          >
            Oggi
          </Button>
          <Button
            size="md"
            variant="ghost"
            className="tap-target px-3 sm:h-9"
            aria-label={core.i18n.t("people.nav.week_next")}
            onClick={() => setWeekStart(addDaysISO(weekStart, 7))}
          >
            <ChevronRight size={16} aria-hidden />
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Ore lavorate" value={`${workload.hoursWorked.toFixed(1)}h`} />
        <StatCard label="Contrattuali" value={`${workload.hoursContractual.toFixed(0)}h`} />
        <StatCard
          label="Delta"
          value={`${workload.deltaHours > 0 ? "+" : ""}${workload.deltaHours.toFixed(1)}h`}
          tone={workload.overAllocation ? "danger" : "neutral"}
        />
        <StatCard
          label="Notti consecutive"
          value={String(workload.consecutiveNights)}
          tone={workload.consecutiveNights > 4 ? "danger" : "neutral"}
        />
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <StatCard label="Giorni in ferie" value={String(workload.daysOnLeave)} />
        <StatCard
          label="Oltre contratto"
          value={workload.overAllocation ? "Sì" : "No"}
          tone={workload.overAllocation ? "danger" : "neutral"}
        />
      </section>

      <section>
        <div className="text-eyebrow mb-2">Turni prossimi</div>
        {upcoming.length === 0 ? (
          <EmptyState title="Nessun turno futuro" size="compact" />
        ) : (
          <ShiftsList shifts={upcoming.slice(0, 8)} deptLabel={deptLabel} />
        )}
      </section>

      <section>
        <div className="text-eyebrow mb-2">Turni recenti</div>
        {past.length === 0 ? (
          <EmptyState title="Nessun turno passato in questa finestra" size="compact" />
        ) : (
          <ShiftsList shifts={past.slice(-8).reverse()} deptLabel={deptLabel} />
        )}
      </section>
    </div>
  );
}

/** "2026-05-25" → "25 mag". Matches the helper in shifts/Schedule.tsx. */
function formatItalianDate(date: string): string {
  const parts = date.split("-").map(Number);
  const m = parts[1] ?? 1;
  const dd = parts[2] ?? 1;
  const months = [
    "gen",
    "feb",
    "mar",
    "apr",
    "mag",
    "giu",
    "lug",
    "ago",
    "set",
    "ott",
    "nov",
    "dic",
  ];
  return `${dd} ${months[m - 1]}`;
}

const slotLabel: Record<"M" | "P" | "N" | "X", string> = {
  M: "Mattino",
  P: "Pomeriggio",
  N: "Notte",
  X: "Riposo",
};
const slotCls: Record<"M" | "P" | "N" | "X", string> = {
  M: "bg-info-soft text-info",
  P: "bg-warning-soft text-warning",
  // Theme inversion (opaque), never a translucent tint hosting text.
  N: "bg-inverse text-fg-inverse",
  X: "bg-subtle text-fg-muted",
};

function ShiftsList({
  shifts,
  deptLabel,
}: {
  shifts: ShiftLite[];
  deptLabel: (hotelId: string | undefined, code: string) => string;
}) {
  return (
    <ul className="space-y-1">
      {shifts.map((s) => (
        <li
          key={s.id}
          className="border border-border-default rounded-md px-3 py-1 flex items-center gap-3 text-sm bg-elevated"
        >
          <span
            className={`px-2 py-0.5 rounded-xs whitespace-nowrap text-mono-xs ${slotCls[s.slot]}`}
          >
            {s.slot} · {slotLabel[s.slot]}
          </span>
          <span className="whitespace-nowrap">{s.date}</span>
          {s.departmentId && (
            <span className="text-mono-xs text-fg-muted">
              · {deptLabel(s.hotelId, s.departmentId)}
            </span>
          )}
          {s.status && <span className="text-xs text-fg-muted ml-auto">{s.status}</span>}
        </li>
      ))}
    </ul>
  );
}
